import { motion } from "framer-motion";
import { useEffect } from "react";

type Props = {};

/**
 * A custom EULA rather than Apple's standard one, because Sidekick ships an
 * auto-renewable subscription and a nutrition-adjacent claim. Apple's Schedule
 * 1 minimum terms are all present below - acknowledgement, scope of licence,
 * maintenance, warranty, product claims, IP, legal compliance, third-party
 * terms, Apple as third-party beneficiary, and contact details - because a
 * custom agreement is rejected without them.
 */
const SidekickToUPage = (props: Props) => {
	useEffect(() => {
		document.title = "Sidekick | Terms of Use";
	}, []);

	return (
		<div className='flex relative overflow-y-scroll overflow-x-hidden gap-y-10 flex-col no-scrollbar items-center justify-center bg-gradient-to-br from-slate-800 via-slate-950 to-slate-800'>
			<motion.div
				className='max-w-4xl mx-auto p-6 bg-slate-800 shadow-md rounded-md my-8 text-slate-100'
				initial={{ opacity: 0, y: 50 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8, ease: "easeOut" }}>
				<motion.h1
					className='text-3xl font-bold mb-4'
					initial={{ opacity: 0, y: -20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.2 }}>
					Terms of Use — Sidekick
				</motion.h1>

				<motion.div
					className='text-slate-200 font-semibold text-lg mb-8 space-y-1'
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ delay: 0.4 }}>
					<p>
						<strong>Effective:</strong> September 1, 2026
					</p>
					<p>
						<strong>Developer:</strong> Softwarify
					</p>
					<p>
						<strong>Contact:</strong> sio@softwarify.co
					</p>
				</motion.div>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
					<h2 className='text-2xl font-semibold mb-2'>1. This Agreement</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						These terms are an agreement between you and Softwarify only, and not with Apple. Softwarify, not Apple, is
						solely responsible for Sidekick and its content. By downloading or using Sidekick you accept these terms; if
						you do not accept them, do not use the app.
					</p>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
					<h2 className='text-2xl font-semibold mb-2'>2. Licence</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						You are granted a non-transferable licence to use Sidekick on any Apple-branded device that you own or
						control, as permitted by the Usage Rules in the Apple Media Services Terms and Conditions. You may not copy,
						reverse-engineer, resell or redistribute the app.
					</p>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
					<h2 className='text-2xl font-semibold mb-2'>3. Subscriptions</h2>
					<ul className='list-disc list-inside text-slate-200 font-semibold text-lg space-y-2'>
						<li>
							Sidekick offers an auto-renewable subscription. The title, duration and price are shown in the app before
							you buy, and the price is charged to your Apple Account at confirmation of purchase.
						</li>
						<li>
							The subscription renews automatically for the same period unless it is cancelled at least 24 hours before
							the end of the current period. Your account is charged for renewal within 24 hours of the period ending.
						</li>
						<li>
							You can manage or cancel the subscription in your Apple Account settings after purchase. Cancelling stops
							the next renewal; the current period is not refunded and unused time is not credited.
						</li>
						<li>Purchases can be restored on any device signed in to the same Apple Account, from Sidekick's settings.</li>
						<li>Refunds are handled by Apple under the Apple Media Services Terms and Conditions.</li>
					</ul>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
					<h2 className='text-2xl font-semibold mb-2'>4. What Sidekick Is Not</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						Sidekick is a general wellness app. It does not diagnose, treat, cure or prevent any disease or condition, it
						does not count calories or macronutrients, and its suggestions are not medical, dietary or nutritional advice.
						Talk to a qualified health professional before making decisions about your health, and never rely on Sidekick
						where you have a food allergy, an intolerance, or any medical condition affected by what you eat.
					</p>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}>
					<h2 className='text-2xl font-semibold mb-2'>5. Your Content</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						What you write in Sidekick stays yours. We store it only to run the app, as described in the{" "}
						<a className='underline hover:text-slate-50' href='https://softwarify.co/apps/sidekick/privacy-policy'>
							Privacy Policy
						</a>
						. A card you share leaves the app only when you tap Share, and what happens to it after that is governed by
						the service you shared it to. You are responsible for what you choose to publish.
					</p>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.0 }}>
					<h2 className='text-2xl font-semibold mb-2'>6. Maintenance and Support</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						Softwarify is solely responsible for any maintenance and support for Sidekick. Apple has no obligation
						whatsoever to furnish any maintenance or support services. Reach us at sio@softwarify.co or through the
						contact form at{" "}
						<a className='underline hover:text-slate-50' href='https://softwarify.co/#contact'>
							softwarify.co
						</a>
						.
					</p>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}>
					<h2 className='text-2xl font-semibold mb-2'>7. Warranty</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						Sidekick is provided “as is”. To the maximum extent permitted by applicable law, Softwarify disclaims all
						warranties, express or implied. In the event of any failure of the app to conform to any applicable warranty,
						you may notify Apple, and Apple will refund the purchase price for the app to you; to the maximum extent
						permitted by law, Apple has no other warranty obligation whatsoever with respect to the app.
					</p>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}>
					<h2 className='text-2xl font-semibold mb-2'>8. Product Claims</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						Softwarify, not Apple, is responsible for addressing any claims relating to Sidekick or your possession and
						use of it, including product liability claims, any claim that the app fails to conform to a legal or
						regulatory requirement, and claims arising under consumer protection or similar legislation.
					</p>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}>
					<h2 className='text-2xl font-semibold mb-2'>9. Intellectual Property</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						In the event of any third-party claim that Sidekick or your possession and use of it infringes that third
						party's intellectual property rights, Softwarify, not Apple, is solely responsible for the investigation,
						defence, settlement and discharge of that claim.
					</p>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}>
					<h2 className='text-2xl font-semibold mb-2'>10. Legal Compliance</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						You represent that you are not located in a country subject to a U.S. Government embargo or designated as a
						“terrorist supporting” country, and that you are not listed on any U.S. Government list of prohibited or
						restricted parties.
					</p>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}>
					<h2 className='text-2xl font-semibold mb-2'>11. Third-Party Terms</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						You must comply with any applicable third-party terms of agreement when using Sidekick, including the terms of
						any service you share a card to.
					</p>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}>
					<h2 className='text-2xl font-semibold mb-2'>12. Apple as Third-Party Beneficiary</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						Apple and its subsidiaries are third-party beneficiaries of these terms and, upon your acceptance, will have
						the right (and will be deemed to have accepted the right) to enforce them against you.
					</p>
				</motion.section>

				<motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.7 }}>
					<h2 className='text-2xl font-semibold mb-2'>13. Termination and Changes</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						You may end this agreement at any time by deleting your account in Sidekick's settings and removing the app.
						We may update these terms; the effective date above will change and continued use after that date means you
						accept the update.
					</p>
				</motion.section>
			</motion.div>
		</div>
	);
};

export default SidekickToUPage;
